import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const callToolsArticle: BlogArticle = {
  key: BlogArticleKey.CALL_ANALYSIS_TOOLS,
  slug: BlogSlugs.CALL_ANALYSIS_TOOLS,
  categoryId: "01",
  date: "2026-07-18",
  readTime: 8,
  locales: {
    fr: {
      title:
        "Quel outil pour analyser ses appels de vente ? (Claap, Leexi, tl;dv, Modjo… et Claude)",
      excerpt:
        "Le marché a explosé, et on s'y perd. Un tour d'horizon honnête des outils, leurs forces, leurs limites, et la vraie question à se poser avant d'en choisir un.",
      description:
        "Le marché a explosé, et on s'y perd. Tu utilises sûrement déjà un outil pour analyser tes appels. Mais est-ce le bon pour ton besoin réel, et surtout, est-ce que tu exploites vraiment ce qu'il en sort ? Tour d'horizon honnête.",
      ctaTitle: "Vois ce que ça donne sur tes vrais appels",
      ctaText:
        "Tu repars avec une lecture concrète de tes propres appels : ce qui revient, ce qui bloque, et ce que cette donnée change pour la suite de ta chaîne. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "En 2026, analyser ses appels de vente n'a jamais été aussi simple. Une dizaine d'outils enregistrent, transcrivent et résument tes conversations en quelques minutes. Le problème n'est plus *de pouvoir le faire*, mais de **choisir l'outil adapté à ton besoin**, et de savoir quoi faire des données une fois qu'elles sont là.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Il n'existe pas de meilleur outil dans l'absolu. Il y a un meilleur outil pour un objectif donné. Voici comment situer les principales options, honnêtement, pour t'aider à choisir.",
        },
        { type: ArticleBlockType.H2, text: "Les trois familles d'outils d'analyse d'appels" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Avant de comparer, il faut comprendre que ces outils ne jouent pas dans la même catégorie :",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Les notetakers** : ils transcrivent et résument. Simples, rapides, parfaits pour ne rien oublier d'une réunion. Exemples : tl;dv, Fathom, Fireflies.",
            "**Les plateformes de conversation intelligence** : elles vont plus loin : détection d'objections, coaching, analyse de performance. Exemples : Modjo, Leexi, Claap.",
            "**Les plateformes de revenue intelligence** : lourdes, pensées pour les grandes équipes commerciales, avec prévisions et pilotage du pipeline. Exemples : Gong, Clari.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Selon que tu es coach indépendant, fondateur, ou manager d'une équipe de dix commerciaux, ton besoin n'est pas le même. Regardons les options concrètes.",
        },
        { type: ArticleBlockType.H2, text: "Le tour des outils, forces et limites" },
        {
          type: ArticleBlockType.TOOL,
          name: "tl;dv",
          tag: "Le plus simple",
          desc: "Un notetaker efficace : transcription, résumés, partage d'extraits vidéo. Très bon pour la collaboration asynchrone et garder une trace propre de chaque appel.",
          limit:
            "il capture, il ne creuse pas. Peu d'analyse commerciale de fond (patterns d'objections sur plusieurs appels, signaux d'achat).",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Fathom",
          tag: "Léger et gratuit",
          desc: "Résumés de réunions et points d'action, intégration rapide à Zoom et Google Meet, configuration minimale. Idéal pour les petites équipes qui veulent l'essentiel sans complexité.",
          limit:
            "fonctionnalités volontairement limitées. Documentation de réunion plus qu'analyse de vente.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Leexi",
          tag: "Simple et conforme",
          desc: "Transcription et suivi de réunions, avec une bonne intégration aux systèmes téléphoniques (VoIP) et un accent sur la sécurité et la conformité. Pratique pour les équipes qui veulent une solution sans fioritures.",
          limit:
            "reste centré sur la transcription et le suivi. L'exploitation avancée des données est limitée.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Modjo",
          tag: "Le coaching d'équipe",
          desc: "Plateforme française, conforme RGPD, avec de solides flux de coaching : tableaux de bord, suivi des objections, analyse de performance, intégration CRM. Pensée pour les équipes commerciales qui veulent progresser.",
          limit:
            "orientée équipe et manager. Moins adaptée au coach indépendant ou au fondateur solo qui gère plusieurs contextes clients différents.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Claap",
          tag: "La vidéo asynchrone",
          desc: "Plutôt un wiki vidéo qu'un moteur d'analyse : bibliothèque d'appels, partage d'extraits, indexation par mots-clés. Excellent pour réduire les réunions et diffuser des démos.",
          limit:
            "l'analyse commerciale de fond n'est pas son cœur. Fort sur le partage, plus léger sur les insights de vente.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Gong / Clari",
          tag: "Les grands comptes",
          desc: "Les poids lourds de la revenue intelligence : analyse à grande échelle, prévisions, pilotage du pipeline. Puissants pour une force de vente structurée.",
          limit:
            "coûteux, longs à déployer (souvent plusieurs semaines de configuration), surdimensionnés si tu n'as pas une grande équipe.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "ChatGPT / Claude",
          tag: "L'option DIY",
          desc: "Tu peux tout à fait coller un transcript dans ChatGPT ou Claude et demander un résumé, les objections, les douleurs. Rapide, flexible, et souvent étonnamment pertinent sur un appel isolé.",
          limit:
            "l'IA générique part de zéro à chaque fois. Elle ne connaît pas ton client, ne garde pas le contexte d'un appel à l'autre, et t'oblige à réexpliquer qui est le prospect à chaque prompt. Tu refais le travail sans arrêt.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ton besoin", "L'option adaptée"],
          rows: [
            ["Ne rien oublier d'une réunion", "tl;dv, Fathom"],
            ["Coacher une équipe commerciale", "Modjo"],
            ["Piloter un gros pipeline", "Gong, Clari"],
            ["Partager des extraits vidéo", "Claap"],
            ["Analyser un appel vite fait", "ChatGPT, Claude"],
          ],
        },
        { type: ArticleBlockType.H2, text: "Le vrai angle mort de tous ces outils" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'Tu as peut-être remarqué le point commun. Tous ces outils **analysent bien**. Certains mieux que d\'autres, mais globalement, la brique "analyse" est devenue un standard.',
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Le problème n'a jamais été d'analyser un appel. C'est ce qui se passe après.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une fois l'analyse faite, où va la donnée ? Dans un transcript. Un tableau de bord. Un résumé que tu lis une fois. Un chat Claude que tu fermes. **Elle meurt, isolée, à l'endroit où elle a été produite.**",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Pourtant, cette donnée vaut de l'or ailleurs. Les objections qui reviennent dans tes appels devraient nourrir ton avatar client. Cet avatar devrait affiner ta recherche de prospects. Ces prospects devraient alimenter tes messages. Les vrais mots de tes clients devraient caler ton contenu.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Au lieu de ça, tu reconstruis chaque brique à la main, pour chaque client, à partir de zéro. C'est là que se perd le vrai temps, et la vraie valeur.",
        },
        { type: ArticleBlockType.H2, text: "La bonne question à se poser" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'Quand on choisit un outil, on demande : "lequel analyse le mieux mes appels ?" C\'est la mauvaise question, ou du moins une question incomplète.',
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La bonne question est : **\"lequel réutilise cette donnée sur toute ma chaîne ?\"** Parce que l'analyse n'est qu'un point de départ. Ce qui change ton quotidien, c'est qu'une donnée captée une fois ressorte partout, sans que tu la ressaisisses.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "C'est exactement là que se place Meidly",
          text: "Meidly analyse tes appels comme les autres : objections, signaux d'achat, verbatims. Mais l'analyse n'est qu'une brique parmi d'autres. La vraie différence, c'est que cette donnée alimente ensuite ton avatar client, ta recherche de prospects, tes messages et ton contenu. Une suite d'outils où une seule donnée circule sur toute la chaîne, au lieu de mourir dans un transcript.",
          linkLabel: "Découvrir l'analyse d'appels de Meidly",
          linkPath: ProtectedPaths.CALL_ANALYSIS,
        },
        { type: ArticleBlockType.H2, text: "Alors, quel outil choisir ?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si ton seul besoin est de garder une trace de tes réunions, un notetaker comme tl;dv ou Fathom suffit. Si tu coaches une équipe, Modjo est solide. Si tu pilotes un gros pipeline, Gong fait le travail.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Mais si ton problème n'est pas *d'analyser*, mais de **transformer ce que disent tes clients en décisions concrètes sur toute ta chaîne commerciale**, comprendre qui achète, trouver les bons prospects, aligner ton discours, alors un outil d'analyse seul ne suffira pas. Il te faut un système où la donnée circule.",
        },
      ],
      faq: [
        {
          question: "Quel est le meilleur outil pour analyser ses appels de vente ?",
          answer:
            "Il n'y a pas de meilleur outil universel. Pour ne rien oublier d'une réunion, un notetaker comme tl;dv ou Fathom suffit. Pour coacher une équipe, Modjo est solide. Pour un gros pipeline, Gong. Le vrai critère n'est pas seulement la qualité de l'analyse, mais ce que tu fais de la donnée ensuite.",
        },
        {
          question: "Peut-on analyser ses appels de vente avec ChatGPT ou Claude ?",
          answer:
            "Oui, coller un transcript dans ChatGPT ou Claude donne souvent de bons résultats sur un appel isolé. La limite, c'est que l'IA générique repart de zéro à chaque fois : elle ne connaît pas ton client, ne garde pas le contexte d'un appel à l'autre, et t'oblige à tout réexpliquer à chaque prompt.",
        },
        {
          question:
            "Quelle est la différence entre un notetaker et une plateforme de conversation intelligence ?",
          answer:
            "Un notetaker (tl;dv, Fathom) transcrit et résume. Une plateforme de conversation intelligence (Modjo, Leexi, Claap) va plus loin : détection d'objections, coaching, analyse de performance sur l'ensemble des appels.",
        },
        {
          question: "Analyser ses appels suffit-il à mieux vendre ?",
          answer:
            "Non. L'analyse est un point de départ. La vraie valeur vient de la réutilisation de cette donnée sur toute ta chaîne commerciale : avatar client, recherche de prospects, messages, contenu. C'est là que la plupart des outils s'arrêtent, et c'est précisément le rôle d'une suite connectée comme Meidly.",
        },
      ],
    },
    en: {
      title: "Which tool to analyze your sales calls? (Leexi, Modjo, tl;dv)",
      excerpt:
        "The market has exploded, and it's easy to get lost. An honest overview of the tools, their strengths, their limits, and the real question to ask before choosing one.",
      description:
        "The market has exploded, and it's easy to get lost. You're probably already using a tool to analyze your calls. But is it the right one for your real need, and above all, are you really making the most of what it produces? An honest overview.",
      ctaTitle: "See what it looks like on your real calls",
      ctaText:
        "You leave with a concrete read of your own calls: what keeps coming back, what stalls, and what that data changes further down your chain. No commitment.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "In 2026, analyzing your sales calls has never been easier. A dozen tools record, transcribe and summarize your conversations in minutes. The problem is no longer *being able to do it*, but **choosing the tool that fits your need**, and knowing what to do with the data once it's there.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "There is no best tool in absolute terms. There is a best tool for a given goal. Here is how to situate the main options, honestly, to help you choose.",
        },
        { type: ArticleBlockType.H2, text: "The three families of call analysis tools" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Before comparing, you need to understand that these tools don't play in the same category:",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Notetakers**: they transcribe and summarize. Simple, fast, perfect for not forgetting anything from a meeting. Examples: tl;dv, Fathom, Fireflies.",
            "**Conversation intelligence platforms**: they go further: objection detection, coaching, performance analysis. Examples: Modjo, Leexi, Claap.",
            "**Revenue intelligence platforms**: heavy, built for large sales teams, with forecasting and pipeline management. Examples: Gong, Clari.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Whether you're an independent coach, a founder, or a manager of a team of ten reps, your need isn't the same. Let's look at the concrete options.",
        },
        { type: ArticleBlockType.H2, text: "A tour of the tools, strengths and limits" },
        {
          type: ArticleBlockType.TOOL,
          name: "tl;dv",
          tag: "The simplest",
          desc: "An effective notetaker: transcription, summaries, video clip sharing. Very good for asynchronous collaboration and keeping a clean record of every call.",
          limit:
            "it captures, it doesn't dig. Little deep sales analysis (objection patterns across calls, buying signals).",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Fathom",
          tag: "Light and free",
          desc: "Meeting summaries and action items, quick integration with Zoom and Google Meet, minimal setup. Ideal for small teams who want the essentials without complexity.",
          limit: "deliberately limited features. Meeting documentation more than sales analysis.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Leexi",
          tag: "Simple and compliant",
          desc: "Transcription and meeting tracking, with good integration to phone systems (VoIP) and a focus on security and compliance. Handy for teams who want a no-frills solution.",
          limit:
            "stays centered on transcription and tracking. Advanced use of the data is limited.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Modjo",
          tag: "Team coaching",
          desc: "A French platform, GDPR compliant, with solid coaching flows: dashboards, objection tracking, performance analysis, CRM integration. Built for sales teams that want to improve.",
          limit:
            "team and manager oriented. Less suited to the solo coach or founder juggling several different client contexts.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Claap",
          tag: "Async video",
          desc: "More of a video wiki than an analysis engine: call library, clip sharing, keyword indexing. Excellent for cutting down meetings and sharing demos.",
          limit:
            "deep sales analysis isn't its core. Strong on sharing, lighter on sales insights.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Gong / Clari",
          tag: "Enterprise",
          desc: "The heavyweights of revenue intelligence: large-scale analysis, forecasting, pipeline management. Powerful for a structured sales force.",
          limit:
            "expensive, slow to deploy (often several weeks of setup), oversized if you don't have a large team.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "ChatGPT / Claude",
          tag: "The DIY option",
          desc: "You can absolutely paste a transcript into ChatGPT or Claude and ask for a summary, the objections, the pains. Fast, flexible, and often surprisingly relevant on a single call.",
          limit:
            "generic AI starts from scratch every time. It doesn't know your customer, doesn't keep context from one call to the next, and forces you to re-explain who the prospect is on every prompt. You redo the work over and over.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Your need", "The right option"],
          rows: [
            ["Not forgetting anything from a meeting", "tl;dv, Fathom"],
            ["Coaching a sales team", "Modjo"],
            ["Managing a large pipeline", "Gong, Clari"],
            ["Sharing video clips", "Claap"],
            ["Analyzing a call quickly", "ChatGPT, Claude"],
          ],
        },
        { type: ArticleBlockType.H2, text: "The real blind spot of all these tools" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'You may have noticed the common thread. All these tools **analyze well**. Some better than others, but overall, the "analysis" building block has become a standard.',
        },
        {
          type: ArticleBlockType.LEAD,
          text: "The problem was never analyzing a call. It's what happens afterwards.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Once the analysis is done, where does the data go? Into a transcript. A dashboard. A summary you read once. A Claude chat you close. **It dies, isolated, where it was produced.**",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Yet this data is worth gold elsewhere. The objections that recur in your calls should feed your customer avatar. That avatar should refine your prospect search. Those prospects should fuel your messages. Your customers' real words should shape your content.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Instead, you rebuild every block by hand, for every client, from scratch. That's where the real time, and the real value, is lost.",
        },
        { type: ArticleBlockType.H2, text: "The right question to ask" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'When choosing a tool, people ask: "which one analyzes my calls best?" That\'s the wrong question, or at least an incomplete one.',
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'The right question is: **"which one reuses that data across my whole chain?"** Because analysis is only a starting point. What changes your day-to-day is data captured once resurfacing everywhere, without you re-entering it.',
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "This is exactly where Meidly sits",
          text: "Meidly analyzes your calls like the others: objections, buying signals, verbatims. But analysis is just one block among others. The real difference is that this data then feeds your customer avatar, your prospect search, your messages and your content. A suite of tools where a single piece of data flows across the whole chain, instead of dying in a transcript.",
          linkLabel: "Discover Meidly's call analysis",
          linkPath: ProtectedPaths.CALL_ANALYSIS,
        },
        { type: ArticleBlockType.H2, text: "So, which tool should you choose?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If your only need is to keep a record of your meetings, a notetaker like tl;dv or Fathom is enough. If you coach a team, Modjo is solid. If you manage a large pipeline, Gong does the job.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "But if your problem isn't *analyzing*, but **turning what your customers say into concrete decisions across your whole sales chain**, understanding who buys, finding the right prospects, aligning your messaging, then an analysis tool alone won't be enough. You need a system where the data flows.",
        },
      ],
      faq: [
        {
          question: "What is the best tool to analyze sales calls?",
          answer:
            "There is no universal best tool. To not forget anything from a meeting, a notetaker like tl;dv or Fathom is enough. To coach a team, Modjo is solid. For a large pipeline, Gong. The real criterion isn't just analysis quality, but what you do with the data next.",
        },
        {
          question: "Can you analyze sales calls with ChatGPT or Claude?",
          answer:
            "Yes, pasting a transcript into ChatGPT or Claude often gives good results on a single call. The limit is that generic AI starts from scratch every time: it doesn't know your customer, doesn't keep context from one call to the next, and forces you to re-explain everything on every prompt.",
        },
        {
          question:
            "What's the difference between a notetaker and a conversation intelligence platform?",
          answer:
            "A notetaker (tl;dv, Fathom) transcribes and summarizes. A conversation intelligence platform (Modjo, Leexi, Claap) goes further: objection detection, coaching, performance analysis across all calls.",
        },
        {
          question: "Is analyzing your calls enough to sell better?",
          answer:
            "No. Analysis is a starting point. The real value comes from reusing that data across your whole sales chain: customer avatar, prospect search, messages, content. That's where most tools stop, and precisely the role of a connected suite like Meidly.",
        },
      ],
    },
  },
};
